import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Layers, CheckCircle2, AlertTriangle, Sparkles, Database, RefreshCw, ChevronDown, ChevronUp, Link2, Grid3X3, Search, Loader2 } from 'lucide-react';
import Button from '../../components/ui/Button';
import Card, { CardBody } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAnalysisStore } from '../../stores/analysisStore';
import type {
  QuestionDataCanvas,
  SystemDataSource,
  ConnectionPoint,
  ConnectionPointType,
  SystemRole,
} from '../../stores/analysisStore';
import {
  getSystemSchemaOverview,
  getEnterpriseSystems,
  SchemaOverviewResponse,
  EnterpriseSystem as ApiEnterpriseSystem
} from '../../lib/api/enterpriseSystemsApi';
import { api, caseAnalysisAPI } from '../../lib/api';
import { QuestionDataCanvasCard } from '../../components/analysis/step3/QuestionDataCanvasCard';
import { CrossSystemOverview } from '../../components/analysis/step3/CrossSystemOverview';
import { ResearchSystemModal } from '../../components/analysis/step3/ResearchSystemModal';
import MappingApprovalQueue, {
  type ValidationSnapshot,
} from '../../components/case-analytics/MappingApprovalQueue';

interface Step3Props {
  caseId: string;
  wizardData: any;
  onNext: () => void;
  onBack: () => void;
}

// Cache for fetched schema data
interface SystemSchemaCache {
  [systemId: string]: SchemaOverviewResponse;
}

// NOTE: canvases are generated server-side by
//   POST /api/question-data-canvas/cases/{caseId}/questions/{questionId}/canvas
// and read back with the matching GET. The former client-side generator
// (categorizeColumns / detectConnectionPoints / determinePrimarySystem /
// generateMultiSystemCanvases) built canvases the backend never saw, so
// batch-save matched nothing and USES_TABLE stayed empty. Do not reintroduce it.

export const Step3DataMapping: React.FC<Step3Props> = ({ caseId, wizardData, onNext, onBack }) => {
  const {
    enterpriseSystems,
    customSystems,
    questionCanvases,
    setQuestionCanvases,
    activeCanvasId,
    setActiveCanvasId,
  } = useAnalysisStore();

  // DataMapping AI-validation state — populated by MappingApprovalQueue via the
  // onValidationChange callback. When the case has zero DataMapping nodes
  // (the canvas-only flow), `total === 0` and the gate is a no-op.
  const [validationSnapshot, setValidationSnapshot] = useState<ValidationSnapshot | null>(null);

  // Mutation to save canvases to Neo4j
  const saveCanvasesMutation = useMutation({
    mutationFn: () => caseAnalysisAPI.batchSaveCanvases(
      caseId,
      questionCanvases.map(c => ({
        canvas_id: c.canvas_id,
        status: c.status || 'draft',
        approved: c.approved || false,
        consultant_notes: c.consultant_notes,
      }))
    ),
    onSuccess: () => {
      toast.success('Data mapping canvases saved successfully');
      onNext();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to save data mapping canvases');
    }
  });

  const handleContinue = () => {
    saveCanvasesMutation.mutate();
  };

  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetchingSchema, setIsFetchingSchema] = useState(false);
  const [schemaCache, setSchemaCache] = useState<SystemSchemaCache>({});
  const [backendSystemsMap, setBackendSystemsMap] = useState<Map<string, ApiEnterpriseSystem>>(new Map());
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'overview'>('cards');
  const [researchModalOpen, setResearchModalOpen] = useState(false);
  const [researchModalSystem, setResearchModalSystem] = useState<{
    name: string;
    existingSystemId?: string;
  } | null>(null);

  const selectedSystems = enterpriseSystems.filter(s => s.selected);

  // Strategic questions come from Neo4j, NOT from `wizardData.questionsData`.
  //
  // The Discovery wizard's AI generator mints browser-local ids
  // (`q_ai_<Date.now()>_<i>`) purely so React has stable keys while the user
  // edits the list; those questions are persisted when the case is created and
  // the backend assigns the real `sq_*` ids. The wizard blob keeps the throwaway
  // ids forever, so generating canvases from it posted `q_ai_*` to
  // .../questions/{questionId}/canvas — which matches no StrategicQuestion node
  // and yielded zero canvases.
  //
  // Step 1 (Discovery Summary) already reads this endpoint for the same reason.
  // A client-minted id is display-only until the server confirms it.
  const {
    data: backendQuestions,
    isLoading: loadingQuestions,
    error: questionsError,
  } = useQuery({
    queryKey: ['case-analysis-questions', caseId],
    queryFn: () => api.get<any[]>(`/case-analysis/cases/${caseId}/questions`),
    enabled: !!caseId,
    staleTime: 5 * 60 * 1000,
  });

  // The wizard blob still supplies the `selected` flags — the consultant's
  // Step-4 choices are not persisted per-question — so intersect the two by
  // question text, which survives the id change. When nothing matches (older
  // cases whose wizard blob is gone) fall back to every persisted question.
  const questions = useMemo(() => {
    const persisted = backendQuestions ?? [];
    if (persisted.length === 0) return [];

    // Neo4j spells the body `questionText`, the wizard blob `question`.
    const bodyOf = (q: any) =>
      String(q?.questionText ?? q?.question ?? q?.text ?? '').trim().toLowerCase();

    const selectedTexts = new Set(
      (wizardData?.questionsData ?? [])
        .filter((q: any) => q.selected)
        .map(bodyOf)
        .filter(Boolean)
    );

    // Order by questionId so the displayed "Q1, Q2, …" tracks sq_001, sq_002, …
    // The endpoint returns them in graph order, which made UI "Q1" land on
    // sq_010 — the numbering was positional and unrelated to the id, so a
    // support conversation about "Q1" pointed at the wrong question. Natural
    // sort keeps sq_2 before sq_10 if the ids are ever unpadded.
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const inIdOrder = (list: any[]) =>
      [...list].sort((a, b) =>
        collator.compare(String(a?.questionId ?? ''), String(b?.questionId ?? '')),
      );

    if (selectedTexts.size === 0) return inIdOrder(persisted);

    const matched = persisted.filter((q: any) => selectedTexts.has(bodyOf(q)));
    return inIdOrder(matched.length > 0 ? matched : persisted);
  }, [backendQuestions, wizardData]);

  // Fetch schema data for all selected systems from backend
  // Returns both schema cache and backend systems map for enrichment
  const fetchSchemaForSystems = useCallback(async (): Promise<{
    cache: SystemSchemaCache;
    systemsMap: Map<string, ApiEnterpriseSystem>;
  }> => {
    const cache: SystemSchemaCache = {};
    const systemsMap = new Map<string, ApiEnterpriseSystem>();

    try {
      const backendResponse = await getEnterpriseSystems({ status: 'published', page_size: 100 });
      const allBackendSystems = backendResponse.systems || [];

      // Resolve by systemId, never by name. Step 2 stores the backend's
      // `system_id` verbatim, so the id is the reliable key. The previous
      // substring/vendor match could bind the wrong node whenever one vendor
      // ships several systems — "SAP S/4HANA" and "SAP SuccessFactors" both
      // satisfied the vendor clause, so whichever appeared first in the list
      // won and its schema was silently used for both.
      const backendSystemsById = new Map(
        allBackendSystems.map((sys: ApiEnterpriseSystem) => [sys.system_id, sys])
      );

      for (const selectedSystem of selectedSystems) {
        const matchingBackendSystem = backendSystemsById.get(selectedSystem.systemId);

        if (!matchingBackendSystem) {
          console.warn(
            `No published backend system for id ${selectedSystem.systemId} (${selectedSystem.name}) — skipping schema fetch.`
          );
        }

        if (matchingBackendSystem) {
          // Store backend system info for enrichment
          systemsMap.set(selectedSystem.systemId, matchingBackendSystem);
          systemsMap.set(selectedSystem.name.toLowerCase(), matchingBackendSystem);

          try {
            const schemaData = await getSystemSchemaOverview(matchingBackendSystem.system_id, 50);
            cache[selectedSystem.systemId] = schemaData;
            console.log(`Fetched schema for ${selectedSystem.name}:`, schemaData.total_tables, 'tables');
          } catch (err) {
            console.warn(`Could not fetch schema for ${selectedSystem.name}:`, err);
          }
        }
      }

      // Also try custom systems - THIS IS KEY for fixing Kinaxis showing as "Custom"
      for (const customSystemName of customSystems) {
        const matchingBackendSystem = allBackendSystems.find(
          (backendSys: ApiEnterpriseSystem) =>
            backendSys.system_name.toLowerCase().includes(customSystemName.toLowerCase()) ||
            customSystemName.toLowerCase().includes(backendSys.system_name.toLowerCase())
        );

        if (matchingBackendSystem) {
          // Store backend system info for enrichment - map by custom system ID and name
          const customId = `custom_${customSystemName}`;
          systemsMap.set(customId, matchingBackendSystem);
          systemsMap.set(customSystemName.toLowerCase(), matchingBackendSystem);

          console.log(`Found backend info for custom system ${customSystemName}:`,
            matchingBackendSystem.vendor, matchingBackendSystem.category);

          try {
            const schemaData = await getSystemSchemaOverview(matchingBackendSystem.system_id, 50);
            cache[customId] = schemaData;
            console.log(`Fetched schema for custom system ${customSystemName}:`, schemaData.total_tables, 'tables');
          } catch (err) {
            console.warn(`Could not fetch schema for custom system ${customSystemName}:`, err);
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch backend systems:', err);
    }

    return { cache, systemsMap };
  }, [selectedSystems, customSystems]);

  // Generate canvases on mount if not already generated.
  // Guarded with a ref so the auto-generate fires at most once — without it,
  // StrictMode's dev double-invoke (and any re-mount) runs handleGenerateCanvases
  // twice, which in turn re-runs fetchSchemaForSystems → duplicate
  // schema-overview / enterprise-systems calls. The manual Generate/Regenerate
  // buttons call handleGenerateCanvases directly and are unaffected.
  //
  // The dep array intentionally includes `questions.length`: the strategic
  // questions are now fetched from Neo4j, so on mount the list is empty while
  // that request is in flight. With an empty dep array the effect saw
  // `questions.length === 0`, skipped, and never ran again — nothing would
  // generate until the user pressed the button. The ref still guarantees a
  // single run once the questions land.
  const hasAutoGeneratedRef = useRef(false);
  useEffect(() => {
    if (hasAutoGeneratedRef.current) return;
    if (loadingQuestions) return;
    if (questionCanvases.length === 0 && questions.length > 0 && (selectedSystems.length > 0 || customSystems.length > 0)) {
      hasAutoGeneratedRef.current = true;
      handleGenerateCanvases();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingQuestions, questions.length]);

  // Fetch schema cache on mount even when canvases exist (to fix "No Research"
  // status display).
  //
  // Loop-prevention rationale: `fetchSchemaForSystems` is a useCallback that
  // depends on `selectedSystems` / `customSystems`. Zustand returns fresh
  // array references on every store update, so the callback's identity churns
  // every render. With nothing selected, `cache` comes back empty, the guard
  // `Object.keys(schemaCache).length === 0` stays true, and the effect
  // re-fires on the next identity change — producing the unbounded
  // /enterprise-systems?status=published refetch storm observed in DevTools.
  //
  // We track "already attempted for this canvas-count" in a ref so the
  // effect runs at most once per canvas regeneration. If the user picks new
  // systems and regenerates canvases, the new canvas count resets the ref
  // and a fresh fetch fires.
  const schemaFetchAttemptedForCountRef = useRef<number>(-1);
  useEffect(() => {
    if (questionCanvases.length === 0) return;
    if (Object.keys(schemaCache).length > 0) return;
    if (schemaFetchAttemptedForCountRef.current === questionCanvases.length) return;
    schemaFetchAttemptedForCountRef.current = questionCanvases.length;

    (async () => {
      try {
        const { cache: newSchemaCache, systemsMap } = await fetchSchemaForSystems();
        setSchemaCache(newSchemaCache);
        setBackendSystemsMap(systemsMap);
      } catch (err) {
        console.warn('Failed to fetch schema cache on mount:', err);
      }
    })();
    // fetchSchemaForSystems intentionally omitted from deps — its identity
    // changes every render due to Zustand-derived inputs, which caused the
    // loop. The ref above gates re-entry; the closure captures the current
    // callback which is sufficient for a one-shot fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionCanvases.length]);

  // Helper to check if a system has data in canvas
  const systemHasCanvasData = useCallback((systemId: string, systemName: string): boolean => {
    return questionCanvases.some(canvas =>
      canvas.data_sources?.some(ds =>
        ds.system_id === systemId ||
        ds.system_name.toLowerCase() === systemName.toLowerCase()
      )
    );
  }, [questionCanvases]);

  // Open research modal for a specific system
  const openResearchModal = (systemName: string, systemId?: string) => {
    // Check if system exists in backend
    const backendSystem = backendSystemsMap.get(systemId || '') || backendSystemsMap.get(systemName.toLowerCase());
    setResearchModalSystem({
      name: systemName,
      existingSystemId: backendSystem?.system_id
    });
    setResearchModalOpen(true);
  };

  // Handle research completion from modal
  const handleResearchStarted = async (systemName: string, jobId?: string) => {
    // Refresh schema cache after a delay to pick up any immediate results
    setTimeout(async () => {
      try {
        const { cache: newSchemaCache, systemsMap } = await fetchSchemaForSystems();
        setSchemaCache(newSchemaCache);
        setBackendSystemsMap(systemsMap);
      } catch (err) {
        console.warn('Failed to refresh schema cache:', err);
      }
    }, 2000);
  };

  /**
   * Normalise a persisted canvas into the store's shape.
   *
   * The API returns the raw Neo4j node, so top-level properties are camelCase
   * (canvasId, totalTables…) while the nested `tables` are already snake_case
   * (they were serialised from the TableMapping model). Roles and connection
   * types use identical string values on both sides, so they pass through.
   */
  const adaptServerCanvas = (raw: any, question: any, index: number): QuestionDataCanvas => {
    const dataSources: SystemDataSource[] = (raw?.data_sources ?? []).map((ds: any) => ({
      system_id: ds.systemId ?? ds.system_id ?? '',
      system_name: ds.systemName ?? ds.system_name ?? 'Unknown System',
      vendor: ds.vendor ?? '',
      functional_area: ds.functionalArea ?? ds.functional_area,
      role: (ds.role ?? 'supporting') as SystemRole,
      role_description: ds.roleDescription ?? ds.role_description,
      // `tables` is the parsed tablesJson — already the store's TableMapping shape.
      tables: Array.isArray(ds.tables) ? ds.tables : [],
      approved: ds.approved ?? false,
    }));

    const connectionPoints: ConnectionPoint[] = (raw?.connection_points ?? []).map((cp: any) => ({
      connection_id: cp.connectionId ?? cp.connection_id ?? '',
      source_system_id: cp.sourceSystemId ?? cp.source_system_id ?? '',
      source_system_name: cp.sourceSystemName ?? cp.source_system_name ?? '',
      source_table: cp.sourceTable ?? cp.source_table ?? '',
      source_column: cp.sourceColumn ?? cp.source_column ?? '',
      target_system_id: cp.targetSystemId ?? cp.target_system_id ?? '',
      target_system_name: cp.targetSystemName ?? cp.target_system_name ?? '',
      target_table: cp.targetTable ?? cp.target_table ?? '',
      target_column: cp.targetColumn ?? cp.target_column ?? '',
      connection_type: (cp.connectionType ?? cp.connection_type ?? 'direct_key') as ConnectionPointType,
      transformation_rule: cp.transformationRule ?? cp.transformation_rule,
      cardinality: cp.cardinality ?? 'many_to_one',
      description: cp.description,
      validated: cp.validated ?? false,
    }));

    const totalTables =
      raw?.totalTables ?? dataSources.reduce((sum, ds) => sum + ds.tables.length, 0);
    const totalColumns =
      raw?.totalColumns ??
      dataSources.reduce(
        (sum, ds) =>
          sum +
          ds.tables.reduce(
            (t, tbl) =>
              t +
              (tbl.columns?.must?.length || 0) +
              (tbl.columns?.should?.length || 0) +
              (tbl.columns?.could?.length || 0),
            0,
          ),
        0,
      );

    return {
      // The SERVER's canvasId — this is what makes batch-save's MATCH succeed.
      canvas_id: raw?.canvasId ?? raw?.canvas_id ?? `canvas_${index + 1}`,
      case_id: raw?.caseId ?? caseId,
      question_id: raw?.questionId ?? question.questionId ?? `q_${index}`,
      // `question` now comes from Neo4j, which spells the body `questionText`;
      // the wizard blob spelled it `question`. Accept either.
      question_text:
        raw?.questionText ?? question.questionText ?? question.question ?? question.text ?? '',
      question_category: raw?.questionCategory ?? question.category ?? question.priority ?? 'General',
      data_sources: dataSources,
      connection_points: connectionPoints,
      status: (raw?.status ?? 'approved') as QuestionDataCanvas['status'],
      approved: raw?.approved ?? true,
      // Mapping approval stays fully automatic; 'flag' is a non-blocking badge.
      validation_verdict: totalTables > 0 ? 'approve' : 'flag',
      total_systems: raw?.totalSystems ?? dataSources.length,
      total_tables: totalTables,
      total_columns: totalColumns,
      total_connections: raw?.totalConnections ?? connectionPoints.length,
      consultant_notes: raw?.consultantNotes ?? raw?.consultant_notes,
      created_at: raw?.createdAt,
      updated_at: raw?.updatedAt,
    };
  };

  /**
   * Generate the canvases SERVER-SIDE, one per strategic question.
   *
   * Previously this built canvases in the browser via
   * `generateMultiSystemCanvases`. Those never reached Neo4j — `batch-save`
   * only MATCHes an existing canvasId, so it updated zero rows and returned
   * success. The result was 0 QuestionDataCanvas nodes, 0 USES_TABLE, an empty
   * Step 4, and a Step 6 with no source columns.
   *
   * The backend generator is now the single source of truth: it persists the
   * canvas, and the Phase-3 projection on batch-save turns tablesJson into
   * (:Case)-[:USES_TABLE]->(:DatabaseTable) with question provenance.
   */
  const handleGenerateCanvases = async () => {
    setIsGenerating(true);
    setFetchError(null);

    try {
      // Still fetch the schema cache — the UI uses it for the per-system
      // "research status" badges, independently of canvas generation.
      setIsFetchingSchema(true);
      const { cache: newSchemaCache, systemsMap } = await fetchSchemaForSystems();
      setSchemaCache(newSchemaCache);
      setBackendSystemsMap(systemsMap);
      setIsFetchingSchema(false);

      // Only real, persisted systems can be mapped: the backend resolves each
      // id to an (:EnterpriseSystem) node. Custom free-text systems have no
      // node and would simply be skipped, so they are not sent.
      const systemIds = selectedSystems
        .map((s) => s.systemId)
        .filter((id): id is string => !!id);

      if (systemIds.length === 0) {
        setFetchError(
          'No researched enterprise systems selected. Go back to Step 2 and pick systems whose schema has been imported.',
        );
        setQuestionCanvases([]);
        return;
      }

      if (!caseId) {
        setFetchError('No case id available — cannot persist canvases.');
        return;
      }

      if (questions.length === 0) {
        setFetchError(
          questionsError
            ? 'Could not load this case\'s strategic questions. Retry, or reopen the case from the Case Library.'
            : 'No strategic questions are saved for this case. Complete the Discovery phase before mapping data.',
        );
        setQuestionCanvases([]);
        return;
      }

      const generated: QuestionDataCanvas[] = [];
      const failures: string[] = [];

      // Sequential on purpose: each call reads every selected system's schema,
      // and firing 9+ of those concurrently hammers Neo4j for no latency win.
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        const questionId = question.questionId;
        if (!questionId) continue;

        // Belt-and-braces: only server-assigned ids address a StrategicQuestion
        // node. `q_ai_*` / `manual_q_*` / `seed_q_*` are minted in the browser
        // for React keys and are display-only. Questions are read from the
        // backend above so this should never fire — it exists so a future path
        // that reintroduces a local id fails loudly here instead of as a
        // silent zero-canvas result.
        if (!/^sq_/.test(questionId)) {
          console.error(
            `Refusing to request a canvas for client-minted question id "${questionId}". ` +
              'Only server-assigned sq_* ids are valid.',
          );
          failures.push(questionId);
          continue;
        }

        try {
          await caseAnalysisAPI.generateQuestionCanvas(caseId, questionId, systemIds);
          const raw = await caseAnalysisAPI.getQuestionCanvas(caseId, questionId);
          generated.push(adaptServerCanvas(raw, question, i));
        } catch (err: any) {
          console.error(`Canvas generation failed for ${questionId}:`, err);
          failures.push(questionId);
        }
      }

      if (generated.length === 0) {
        setFetchError(
          'Could not generate any data canvases. Check that the selected systems have imported schemas.',
        );
      } else if (failures.length > 0) {
        setFetchError(
          `${generated.length} of ${questions.length} canvases generated. Failed: ${failures.join(', ')}.`,
        );
      }

      setQuestionCanvases(generated);
    } catch (err) {
      console.error('Error generating canvases:', err);
      setFetchError('Failed to generate data canvases. Please try again.');
    } finally {
      setIsFetchingSchema(false);
      setIsGenerating(false);
    }
  };


  const handleCanvasClick = (canvasId: string) => {
    setActiveCanvasId(canvasId);
    setViewMode('cards');
  };

  // Summary stats
  const stats = useMemo(() => {
    const approved = questionCanvases.filter(c => c.approved).length;
    const totalConnections = questionCanvases.reduce((sum, c) => sum + c.total_connections, 0);
    const uniqueSystems = new Set(questionCanvases.flatMap(c => c.data_sources.map(ds => ds.system_id))).size;
    return { approved, totalConnections, uniqueSystems };
  }, [questionCanvases]);

  // "Recommended starting point" — pick the canvas the consultant should
  // review first.  Same bias-resistant pattern as Step 0 / Step 2:
  // composite score of objective signals + clear-winner margin gate.
  //
  // Scoring (sums to 1.0):
  //   35 % primary source quality   — does the primary source actually carry
  //                                   table data?  A primary with 0 tables
  //                                   guarantees Step 4 schema generation
  //                                   has nothing to work with.
  //   25 % data coverage            — fraction of data sources with >=1 table.
  //                                   High coverage → confident mapping.
  //   20 % column richness          — total_columns (capped at 30 for max).
  //                                   More columns → richer DDL downstream.
  //   15 % cross-system connections — defined connection_points indicate
  //                                   multi-system insight already drawn.
  //   5 %  system breadth           — total_systems capped at 5; small
  //                                   contribution because more isn't
  //                                   always better.
  //
  // Hard disqualifier: no primary source set → the canvas can't be the
  // recommended starting point regardless of other signals.
  //
  // Clear-winner margin: 0.05 (matches Step 0/2).  Strong → emerald chip,
  // marginal → amber "Top match" chip.
  const recommendedCanvas = useMemo<
    | { canvasId: string; reasons: string[]; confidence: 'strong' | 'marginal' }
    | null
  >(() => {
    // Only score unapproved canvases.  Once a canvas is approved the
    // consultant has already acted on the recommendation, so the chip
    // shifts to the next-best UNapproved canvas — gives a moving "review
    // this next" guide as the consultant works through the queue, instead
    // of leaving a stale chip on something already decided.
    const pending = questionCanvases.filter((c) => c.status !== 'approved');
    if (pending.length < 2) return null;

    const scored = pending.map((canvas) => {
      const primary = canvas.data_sources.find((ds) => ds.role === 'primary');
      if (!primary) return { canvasId: canvas.canvas_id, score: 0, reasons: [] as string[] };

      const reasons: string[] = [];

      // Primary source quality
      const primaryTables = primary.tables?.length || 0;
      let primaryScore = 0;
      if (primaryTables >= 3) {
        primaryScore = 1;
        reasons.push(
          `Primary source quality — "${primary.system_name}" already has ${primaryTables} tables mapped, so Step 4 schema generation has real data to work from.`,
        );
      } else if (primaryTables >= 1) {
        primaryScore = 0.6;
        reasons.push(
          `Primary source partially mapped — "${primary.system_name}" has ${primaryTables} table${primaryTables === 1 ? '' : 's'} (more would help downstream).`,
        );
      } else {
        primaryScore = 0.3;
        // Don't add to reasons — this is a weakness, not a selling point
      }

      // Data coverage — fraction of data sources with at least one table
      const sourcesWithTables = canvas.data_sources.filter(
        (ds) => (ds.tables?.length || 0) > 0,
      ).length;
      const totalSources = canvas.data_sources.length || 1;
      const coverage = sourcesWithTables / totalSources;
      if (sourcesWithTables >= 2) {
        reasons.push(
          `Data coverage — ${sourcesWithTables} of ${totalSources} systems have table data (${Math.round(coverage * 100)}% coverage), giving the strongest multi-system view of the question.`,
        );
      }

      // Column richness — capped at 30 for max
      const columnScore = Math.min(canvas.total_columns, 30) / 30;
      if (canvas.total_columns >= 10) {
        reasons.push(
          `Column detail — ${canvas.total_columns} columns mapped across all sources, which translates to a more complete Cypher DDL in Step 4.`,
        );
      }

      // Cross-system connections — capped at 5
      const connectionScore = Math.min(canvas.total_connections, 5) / 5;
      if (canvas.total_connections > 0) {
        reasons.push(
          `Cross-system insight — ${canvas.total_connections} connection${canvas.total_connections === 1 ? '' : 's'} defined between systems, so the Step 4 schema captures relationships, not just isolated tables.`,
        );
      }

      // System breadth — small contribution
      const breadthScore = Math.min(canvas.total_systems, 5) / 5;

      const score =
        primaryScore * 0.35 +
        coverage * 0.25 +
        columnScore * 0.20 +
        connectionScore * 0.15 +
        breadthScore * 0.05;

      return { canvasId: canvas.canvas_id, score, reasons };
    });

    scored.sort((a, b) => b.score - a.score);
    const top = scored[0];
    if (!top || top.score === 0) return null;

    const margin = scored.length > 1 ? top.score - scored[1].score : 1;
    const confidence: 'strong' | 'marginal' = margin >= 0.05 ? 'strong' : 'marginal';

    // If no real positive reasons collected (e.g., all canvases are weak),
    // give the consultant a fallback line so the tooltip isn't empty.
    const reasons =
      top.reasons.length > 0
        ? top.reasons
        : ['Highest composite score across primary quality, coverage, columns and connections — though the field is weak overall.'];

    return { canvasId: top.canvasId, reasons, confidence };
  }, [questionCanvases]);

  // Mapping approval is fully automatic. Canvases are auto-approved on
  // generation and the AI critic validates the backend DataMapping nodes in
  // the background — the consultant never approves anything. The only gate is
  // "AI validation complete": when the case has generated DataMapping nodes,
  // wait until they're all AI-validated; when none exist (`total === 0`, the
  // canvas-only flow) the gate is a no-op.
  const canProceed =
    !validationSnapshot ||
    validationSnapshot.total === 0 ||
    validationSnapshot.validationComplete;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-neutral-900 mb-2">
          Multi-System Data Mapping
        </h2>
        <p className="text-neutral-600">
          Map strategic questions to multiple enterprise systems with cross-system connections
        </p>
      </div>

      {/* Fetch Error Display */}
      {fetchError && (
        <Card className="bg-amber-50 border-amber-200">
          <CardBody>
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">{fetchError}</p>
                <p className="text-sm text-amber-700">
                  Systems without researched schema data will show placeholder mappings.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Generation Status */}
      {isGenerating ? (
        <Card className="bg-gradient-to-r from-blue-50 to-purple-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <div className="animate-spin">
                {isFetchingSchema ? (
                  <Database className="w-6 h-6 text-blue-600" />
                ) : (
                  <Sparkles className="w-6 h-6 text-purple-600" />
                )}
              </div>
              <div className="flex-1">
                <p className="font-semibold text-neutral-900 mb-1">
                  {isFetchingSchema
                    ? 'Fetching Schema Data from Enterprise Systems...'
                    : 'Generating Multi-System Data Canvases...'}
                </p>
                <p className="text-sm text-neutral-600">
                  {isFetchingSchema
                    ? 'Retrieving real table and column data from researched systems.'
                    : 'Mapping questions to multiple systems and detecting cross-system connections...'}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          {/* Summary Stats */}
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50">
            <CardBody>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Layers className="w-6 h-6 text-amber-600" />
                  <div>
                    <p className="font-semibold text-neutral-900">
                      {questionCanvases.length} Question Canvases Generated
                    </p>
                    <p className="text-sm text-neutral-600">
                      {stats.approved} AI-validated • {stats.uniqueSystems} systems • {stats.totalConnections} cross-system connections
                    </p>
                  </div>
                </div>
                {canProceed ? (
                  <CheckCircle2 className="w-8 h-8 text-green-600" />
                ) : (
                  <AlertTriangle className="w-8 h-8 text-amber-600" />
                )}
              </div>
            </CardBody>
          </Card>

          {/* System Schema Status */}
          {questionCanvases.length > 0 && (
            <>
              {/* Warning for unresearched systems */}
              {(() => {
                // Helper to check if system has data in canvases
                const systemHasCanvasData = (systemId: string, systemName: string) => {
                  return questionCanvases.some(canvas =>
                    canvas.data_sources?.some(ds =>
                      (ds.system_id === systemId || ds.system_name.toLowerCase() === systemName.toLowerCase()) &&
                      ds.tables && ds.tables.length > 0
                    )
                  );
                };

                // Check both schemaCache AND canvas data
                const unreseachedSystems = [
                  ...selectedSystems
                    .filter(sys => !schemaCache[sys.systemId] && !systemHasCanvasData(sys.systemId, sys.name))
                    .map(sys => sys.name),
                  ...customSystems
                    .filter(name => !schemaCache[`custom_${name}`] && !systemHasCanvasData(`custom_${name}`, name))
                ];
                if (unreseachedSystems.length > 0) {
                  return (
                    <Card className="bg-amber-50 border-amber-200 mb-4">
                      <CardBody className="py-3">
                        <div className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1">
                            <p className="font-medium text-amber-900">
                              {unreseachedSystems.length} system{unreseachedSystems.length > 1 ? 's' : ''} without research data
                            </p>
                            <p className="text-sm text-amber-700 mt-1">
                              <strong>{unreseachedSystems.join(', ')}</strong> - Limited data mapping available.
                              Click the "Research" button next to each system to gather schema data.
                            </p>
                          </div>
                        </div>
                      </CardBody>
                    </Card>
                  );
                }
                return null;
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {selectedSystems.map(sys => {
                  // Check if system has data from schema cache OR from canvas data
                  const hasSchemaData = !!schemaCache[sys.systemId];
                  const canvasTableCount = questionCanvases.reduce((total, canvas) => {
                    const ds = canvas.data_sources?.find(d =>
                      d.system_id === sys.systemId || d.system_name.toLowerCase() === sys.name.toLowerCase()
                    );
                    return total + (ds?.tables?.length || 0);
                  }, 0);
                  const hasData = hasSchemaData || canvasTableCount > 0;
                  const tableCount = schemaCache[sys.systemId]?.total_tables || canvasTableCount;

                  return (
                    <div
                      key={sys.systemId}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
                        hasData
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {hasData ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <span className="font-medium">{sys.name}</span>
                        {hasData && (
                          <span className="text-xs">
                            ({tableCount} tables)
                          </span>
                        )}
                      </div>
                      {!hasData && (
                        <button
                          onClick={() => openResearchModal(sys.name, sys.systemId)}
                          className="px-2 py-1 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded transition-colors flex items-center gap-1"
                        >
                          <Search className="w-3 h-3" />
                          Research
                        </button>
                      )}
                    </div>
                  );
                })}
                {customSystems.map(name => {
                  const customId = `custom_${name}`;
                  const hasSchemaData = !!schemaCache[customId];
                  const canvasTableCount = questionCanvases.reduce((total, canvas) => {
                    const ds = canvas.data_sources?.find(d =>
                      d.system_id === customId || d.system_name.toLowerCase() === name.toLowerCase()
                    );
                    return total + (ds?.tables?.length || 0);
                  }, 0);
                  const hasData = hasSchemaData || canvasTableCount > 0;
                  const tableCount = schemaCache[customId]?.total_tables || canvasTableCount;

                  return (
                    <div
                      key={name}
                      className={`px-3 py-2 rounded-lg text-sm flex items-center justify-between ${
                        hasData
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {hasData ? (
                          <CheckCircle2 className="w-4 h-4" />
                        ) : (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <span className="font-medium">{name}</span>
                        <Badge variant="outline" className="text-xs">Custom</Badge>
                        {hasData && (
                          <span className="text-xs">
                            ({tableCount} tables)
                          </span>
                        )}
                      </div>
                      {!hasData && (
                        <button
                          onClick={() => openResearchModal(name, customId)}
                          className="px-2 py-1 text-xs font-medium bg-amber-100 hover:bg-amber-200 text-amber-800 rounded transition-colors flex items-center gap-1"
                        >
                          <Search className="w-3 h-3" />
                          Research
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* View Toggle & Regenerate */}
          {questionCanvases.length > 0 && (
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('cards')}
                  className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                    viewMode === 'cards'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Layers className="w-4 h-4" />
                  Question Cards
                </button>
                <button
                  onClick={() => setViewMode('overview')}
                  className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-1.5 transition-colors ${
                    viewMode === 'overview'
                      ? 'bg-white shadow-sm text-gray-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                  System Overview
                </button>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateCanvases}
                disabled={isGenerating}
                className="flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                Regenerate
              </Button>
            </div>
          )}

          {/* Content based on view mode */}
          {questionCanvases.length > 0 ? (
            viewMode === 'cards' ? (
              <div className="space-y-6">
                {questionCanvases.map((canvas, idx) => {
                  const isRecommended = recommendedCanvas?.canvasId === canvas.canvas_id;
                  return (
                    <QuestionDataCanvasCard
                      key={canvas.canvas_id}
                      canvas={canvas}
                      questionNumber={idx + 1}
                      expanded={activeCanvasId === canvas.canvas_id}
                      isRecommended={isRecommended}
                      recommendationConfidence={
                        isRecommended ? recommendedCanvas?.confidence : undefined
                      }
                      recommendationReasons={
                        isRecommended ? recommendedCanvas?.reasons : undefined
                      }
                    />
                  );
                })}
              </div>
            ) : (
              <CrossSystemOverview
                canvases={questionCanvases}
                onCanvasClick={handleCanvasClick}
              />
            )
          ) : (
            <Card>
              <CardBody>
                <div className="text-center py-12">
                  <Database className="w-16 h-16 text-neutral-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-neutral-900 mb-2">
                    No Data Canvases Generated Yet
                  </h3>
                  <p className="text-neutral-600 mb-6">
                    {questions.length === 0
                      ? 'No strategic questions found from Discovery phase'
                      : selectedSystems.length === 0 && customSystems.length === 0
                      ? 'No enterprise systems selected in Step 2'
                      : 'Click the button below to generate multi-system data canvases'}
                  </p>
                  {questions.length > 0 && (selectedSystems.length > 0 || customSystems.length > 0) && (
                    <Button onClick={handleGenerateCanvases} disabled={isGenerating}>
                      <Database className="w-4 h-4 mr-2" />
                      Generate Multi-System Canvases
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          )}

          {/* DataMapping AI-validation panel — read-only. Surfaces individual
              source-column → target-property mappings generated by
              `/case-analysis/cases/{id}/mappings/generate`, each with the AI
              critic's verdict. When the case hasn't run that endpoint (the
              canvas-only flow), the panel renders its empty state and the gate
              stays a no-op. The "Continue" gate waits until background
              validation is complete. */}
          <MappingApprovalQueue
            caseId={caseId}
            onValidationChange={setValidationSnapshot}
          />

          {/* Help Text */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex gap-3">
              <Link2 className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-2">Multi-System Data Mapping</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Each question maps to <strong>multiple enterprise systems</strong></li>
                  <li>Systems have roles: <strong>Primary</strong> (main data), <strong>Supporting</strong> (context), <strong>Reference</strong> (master data)</li>
                  <li><strong>Cross-system connections</strong> show how data links between systems</li>
                  <li>Columns are prioritized: <strong>Must Have</strong> (keys) → <strong>Should Have</strong> (business) → <strong>Could Have</strong> (additional)</li>
                  <li>Click <strong>System Overview</strong> to see all systems and their connections</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* AI-validation status strip — read-only. Mapping approval is fully
          automatic; this just tells the consultant whether the background AI
          validation has finished (and therefore why "Continue" is enabled or
          still waiting). Hidden when there are no canvases yet. */}
      {questionCanvases.length > 0 && (() => {
        const total = validationSnapshot?.total ?? 0;
        const validated = validationSnapshot?.aiValidated ?? 0;
        const flagged = validationSnapshot?.flagged ?? 0;
        // Complete when there are no backend mappings to validate yet, or all
        // of them have been validated — matches the `canProceed` gate above.
        const complete = canProceed;
        const pct = total > 0 ? Math.min(100, Math.round((validated / total) * 100)) : 100;
        return (
          <div
            className={`mt-6 p-4 rounded-lg border ${complete ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {complete ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                ) : (
                  <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
                )}
                <span className={`font-semibold ${complete ? 'text-emerald-900' : 'text-amber-900'}`}>
                  AI validation
                </span>
              </div>
              {total > 0 && (
                <span className={`text-sm font-medium ${complete ? 'text-emerald-800' : 'text-amber-800'}`}>
                  {validated} / {total} validated
                  {flagged > 0 ? ` · ${flagged} flagged` : ''}
                </span>
              )}
            </div>
            <div className="w-full h-2 bg-white/70 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className={`text-xs mt-2 ${complete ? 'text-emerald-800' : 'text-amber-800'}`}>
              {complete
                ? 'The AI has validated and approved your mappings — no manual approval needed. Continue to Step 4 Schema Design.'
                : 'The AI is validating your mappings in the background. This unlocks Step 4 automatically when it finishes.'}
            </p>
          </div>
        );
      })()}

      {/* Navigation */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canProceed || isGenerating || saveCanvasesMutation.isPending}
        >
          {saveCanvasesMutation.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Continue to Schema Design'
          )}
        </Button>
      </div>

      {/* Research System Modal */}
      <ResearchSystemModal
        isOpen={researchModalOpen}
        onClose={() => {
          setResearchModalOpen(false);
          setResearchModalSystem(null);
        }}
        systemName={researchModalSystem?.name || ''}
        existingSystemId={researchModalSystem?.existingSystemId}
        onResearchStarted={handleResearchStarted}
      />
    </div>
  );
};

export default Step3DataMapping;
